import { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { client } from '@/services/api/axios.ts';

export type TPlantEvent = {
  eventId: string;
  createdAt: string;
  type: 'Info' | 'Major';
  description: string;
  source: string;
  sourceId: string;
};

interface EventsParams {
  plantId?: string;
  limit?: number;
}

interface PageParam {
  nextCursor?: string;
  currentPage: number;
}

export interface EventsResponse {
  length: number;
  body: TPlantEvent[];
  nextPage?: number | null;
  nextCursor?: string | null;
  currentPage?: number;
}

async function fetchEvents(
  pageParam: PageParam,
  { limit = 10, plantId = '' }: EventsParams
): Promise<EventsResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    currentPage: String(pageParam.currentPage),
    ...(pageParam.nextCursor && { nextCursor: pageParam.nextCursor }),
  });

  try {
    const { data } = await client.get<EventsResponse>(`/events/plants/${plantId}?${params}`);
    return data;
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message
      : error instanceof Error
        ? error.message
        : 'An unknown error occurred';
    throw new Error(message);
  }
}

export function useGetPlantEventsQuery(
  options: EventsParams = { limit: 10, plantId: '' },
  config?: UseInfiniteQueryOptions<EventsResponse, Error, TPlantEvent[], string[], PageParam>
) {
  const plantId = options.plantId || '';

  return useInfiniteQuery({
    ...config,
    queryKey: ['plants', plantId, 'events'],
    queryFn: ({ pageParam }) => fetchEvents(pageParam, options),
    initialPageParam: { nextCursor: undefined, currentPage: 0 },
    getNextPageParam: (lastPage) =>
      lastPage?.nextPage && lastPage?.nextCursor
        ? { nextCursor: lastPage.nextCursor, currentPage: lastPage.nextPage }
        : undefined,
    select: (data) => ({
      ...data,
      pages: data.pages.flatMap((page) => page.body),
      isLastPage: !Boolean(data.pages.at(-1)?.nextCursor),
    }),
    staleTime: 60_000,
    retry: 1,
  });
}
